import { useEffect, useMemo, useRef, useState } from "react";
import {
  Edit2,
  Trash2,
  MoreHorizontal,
  Save,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Eye,
} from "lucide-react";
import {
  despesaService,
  grupoService,
  subGrupoService,
} from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import PageContainer from "../../components/PageContainer";
import {
  Button,
  FormField,
  SelectInput,
  TextAreaInput,
  TextInput,
} from "../../components/ui";
import { formatarData, formatarDataDaAPI } from "../../utils/FormatarData";
import { formatarValorReal, formatCurrencyMoeda } from "../../utils/FormatarValores";
import {
  obterHojeLocalISO,
  obterUltimoDiaDoMesLocalISO,
  periodoEstaInvertido,
} from "../../utils/DataLocal";

// Período aplicado ao abrir ou recarregar a tela: de hoje até o fim do mês.
const criarFiltrosIniciais = () => ({
  descricao: "",
  situacao: "",
  valorMin: "",
  valorMax: "",
  campoData: "DATA_VENCIMENTO",
  dataDe: obterHojeLocalISO(),
  dataAte: obterUltimoDiaDoMesLocalISO(),
  grupoId: "",
  subGrupoId: "",
});

const criarFiltrosLimpos = () => ({
  descricao: "",
  situacao: "",
  valorMin: "",
  valorMax: "",
  campoData: "DATA_VENCIMENTO",
  dataDe: "",
  dataAte: "",
  grupoId: "",
  subGrupoId: "",
});

const criarFormularioInicial = () => ({
  subgrupo: { id: "" },
  descricao: "",
  dataVencimento: obterHojeLocalISO(),
  qtdParcela: 1,
  valorTotal: 0,
  valorTotalFormatted: "0,00",
  observacao: "",
});

const Despesa = () => {
  const [despesas, setDespesas] = useState([]);
  const [resumoDespesas, setResumoDespesas] = useState({
    sumValorDespesa: 0,
    sumValorPago: 0,
    sumJuros: 0,
    sumPendente: 0,
  });
  const [subGrupos, setSubGrupos] = useState([]);
  const [subGruposFiltrados, setSubGruposFiltrados] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [grupoSelecionadoId, setGrupoSelecionadoId] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Filtros aplicados na lista e nos totais.
  const [filtros, setFiltros] = useState(criarFiltrosIniciais);
  // Filtros em edição no painel; só valem para a API ao clicar em Buscar.
  const [filtrosEmEdicao, setFiltrosEmEdicao] = useState(criarFiltrosIniciais);
  const [paginacao, setPaginacao] = useState({
    paginaAtual: 1,
    itensPorPagina: 6,
  });
  const [formData, setFormData] = useState(criarFormularioInicial);
  const envioEmAndamentoRef = useRef(false);
  const formularioRef = useRef(null);
  const [showPagamentoForm, setShowPagamentoForm] = useState(false);
  const [pagamentoItem, setPagamentoItem] = useState(null);
  const [pagamentoData, setPagamentoData] = useState({
    dataPagamento: new Date().toLocaleDateString("en-CA"),
    valorPago: 0,
    valorPagoFormatted: "0,00",
  });
  const [observacaoModalOpen, setObservacaoModalOpen] = useState(false);
  const [observacaoSelecionada, setObservacaoSelecionada] = useState("");
  const [acaoMenuAbertoId, setAcaoMenuAbertoId] = useState(null);
  const [acaoMenuPos, setAcaoMenuPos] = useState(null);
  const [acaoMenuDirecao, setAcaoMenuDirecao] = useState("down");
  const [confirmarDeletarMassaModalOpen, setConfirmarDeletarMassaModalOpen] = useState(false);
  const [despesasParaDeletarMassa, setDespesasParaDeletarMassa] = useState([]);
  const [parcelaRefSelecionada, setParcelaRefSelecionada] = useState(null);

  const { showSuccess, showError } = useNotification();
  const confirmDialog = useConfirmDialog();

  const normalizarValorFiltro = (valor) => {
    if (valor === null || valor === undefined) {
      return "";
    }

    return String(valor).trim();
  };

  const verificarFiltrosAplicados = (filtrosParaValidar = {}) => {
    const valorMinNumero = parseValorMoedaParaNumero(filtrosParaValidar.valorMin);
    const valorMaxNumero = parseValorMoedaParaNumero(filtrosParaValidar.valorMax);

    return (
      normalizarValorFiltro(filtrosParaValidar.descricao) !== "" ||
      normalizarValorFiltro(filtrosParaValidar.situacao) !== "" ||
      normalizarValorFiltro(filtrosParaValidar.grupoId) !== "" ||
      normalizarValorFiltro(filtrosParaValidar.subGrupoId) !== "" ||
      (valorMinNumero !== "" && Number(valorMinNumero) > 0) ||
      (valorMaxNumero !== "" && Number(valorMaxNumero) > 0) ||
      normalizarValorFiltro(filtrosParaValidar.dataDe) !== "" ||
      normalizarValorFiltro(filtrosParaValidar.dataAte) !== ""
    );
  };

  const loadDespesas = async (filtrosParam = filtros) => {
    setLoading(true);
    try {
      const data = await despesaService.listarDespesas(filtrosParam);

      if (Array.isArray(data)) {
        setDespesas(data);
        setResumoDespesas({
          sumValorDespesa: 0,
          sumValorPago: 0,
          sumJuros: 0,
          sumPendente: 0,
        });
      } else {
        setDespesas(data?.items || []);
        setResumoDespesas({
          sumValorDespesa: data?.summary?.sumValorDespesa || 0,
          sumValorPago: data?.summary?.sumValorPago || 0,
          sumJuros: data?.summary?.sumJuros || 0,
          sumPendente: data?.summary?.sumPendente || 0,
        });
      }

      setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const loadSubGrupos = async () => {
    try {
      const data = await subGrupoService.listarSubGrupo();
      setSubGrupos(data || []);
    } catch (err) {}
  };

  const loadSubGruposPorGrupo = async (grupoId) => {
    try {
      const data = await subGrupoService.listarSubGrupoPorGrupo(grupoId);
      const filtrados = (data || []).filter((subGrupo) => {
        const grupoIdDoSubGrupo =
          subGrupo?.grupo?.id ?? subGrupo?.grupoId ?? "";
        return String(grupoIdDoSubGrupo) === String(grupoId);
      });
      setSubGruposFiltrados(filtrados);
    } catch (err) {
      setSubGruposFiltrados([]);
    }
  };

  const loadGrupos = async () => {
    try {
      const data = await grupoService.listarGrupo();
      setGrupos(data || []);
    } catch (err) {}
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;

    if (name === "valorMin" || name === "valorMax") {
      const { formatted } = formatarValorReal(value);
      setFiltrosEmEdicao((prev) => ({
        ...prev,
        [name]: value ? formatted : "",
      }));
      return;
    }

    if (name === "grupoId") {
      // O subgrupo depende do grupo: trocar o grupo limpa o subgrupo anterior.
      setFiltrosEmEdicao((prev) => ({
        ...prev,
        grupoId: value,
        subGrupoId: "",
      }));
      return;
    }

    setFiltrosEmEdicao((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const parseValorMoedaParaNumero = (valor) => {
    if (valor === "" || valor === null || valor === undefined) {
      return "";
    }

    if (typeof valor === "number") {
      return valor;
    }

    const valorString = String(valor).trim();
    if (!valorString) {
      return "";
    }

    const valorNormalizado = valorString.includes(",")
      ? valorString.replace(/\./g, "").replace(",", ".")
      : valorString;

    const numero = Number(valorNormalizado);
    if (!Number.isNaN(numero)) {
      return numero;
    }

    return formatarValorReal(valorString).numeric;
  };

  const handleBuscarFiltros = () => {
    if (periodoEstaInvertido(filtrosEmEdicao.dataDe, filtrosEmEdicao.dataAte)) {
      showError("A data de vencimento inicial não pode ser posterior à final");
      return;
    }

    const novosFiltros = {
      ...filtrosEmEdicao,
      valorMin: parseValorMoedaParaNumero(filtrosEmEdicao.valorMin),
      valorMax: parseValorMoedaParaNumero(filtrosEmEdicao.valorMax),
      campoData: "DATA_VENCIMENTO",
    };

    setFiltros(novosFiltros);
    loadDespesas(novosFiltros);
  };

  const handleLimparFiltros = () => {
    const filtrosLimpos = criarFiltrosLimpos();

    setFiltrosEmEdicao(filtrosLimpos);
    setFiltros(filtrosLimpos);
    loadDespesas(filtrosLimpos);
  };

  const toInputDate = (value) => {
    if (!value) return "";

    if (Array.isArray(value) && value.length >= 3) {
      const [year, month, day] = value;
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
        2,
        "0"
      )}`;
    }

    if (typeof value === "string") {
      if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return value;
      }

      if (value.includes("/")) {
        return formatarDataDaAPI(value);
      }
    }

    const dateObj = new Date(value);
    if (Number.isNaN(dateObj.getTime())) {
      return "";
    }

    return dateObj.toLocaleDateString("en-CA");
  };

  const getSituacaoClasseCor = (situacao) => {
    const situacaoNormalizada = String(situacao || "").trim().toUpperCase();

    if (situacaoNormalizada === "PAGO") {
      return "text-green-600";
    }

    if (situacaoNormalizada === "PENDENTE") {
      return "text-yellow-600";
    }

    if (situacaoNormalizada === "ATRASADO") {
      return "text-red-600";
    }

    return "text-gray-700";
  };

  const totalRegistros = despesas.length;
  const totalPaginas = Math.ceil(totalRegistros / paginacao.itensPorPagina);
  const indiceInicial = (paginacao.paginaAtual - 1) * paginacao.itensPorPagina;
  const indiceFinal = indiceInicial + paginacao.itensPorPagina;
  const despesasPaginadas = despesas.slice(indiceInicial, indiceFinal);

  const filtrosAplicadosNaLista = verificarFiltrosAplicados(filtros);

  const despesasExibidas = filtrosAplicadosNaLista ? despesas : despesasPaginadas;

  const irParaPrimeiraPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: totalPaginas }));
  };

  const irParaPaginaAnterior = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPagina = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginas, prev.paginaAtual + 1),
    }));
  };

  const irParaPagina = (numeroPagina) => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: numeroPagina }));
  };

  const temFiltrosAtivos = filtrosAplicadosNaLista;

  const gerarNumerosPaginas = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacao.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(totalPaginas, inicio + maxPaginasVisiveis - 1);

    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "subgrupo") {
      setFormData((prev) => ({
        ...prev,
        subgrupo: { id: value },
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGrupoChange = (e) => {
    setGrupoSelecionadoId(e.target.value);
    setFormData((prev) => ({
      ...prev,
      subgrupo: { id: "" },
    }));
  };

  const handleValorTotalChange = (e) => {
    const { formatted, numeric } = formatarValorReal(e.target.value);
    setFormData((prev) => ({
      ...prev,
      valorTotal: numeric,
      valorTotalFormatted: formatted,
    }));
  };

  const handlePagamentoValorChange = (e) => {
    const { formatted, numeric } = formatarValorReal(e.target.value);
    setPagamentoData((prev) => ({
      ...prev,
      valorPago: numeric,
      valorPagoFormatted: formatted,
    }));
  };

  const getQtdParcelaDoItem = (item) => {
    if (item?.qtdParcela) {
      const qtd = Number(item.qtdParcela);
      return Number.isNaN(qtd) || qtd <= 0 ? 1 : qtd;
    }

    if (typeof item?.parcela === "string" && item.parcela.includes("/")) {
      const partes = item.parcela.split("/");
      const totalParcela = Number(partes[1]);
      if (!Number.isNaN(totalParcela) && totalParcela > 0) {
        return totalParcela;
      }
    }

    return 1;
  };

  const getValorDespesaCalculadoFormulario = () => {
    const qtdParcela = Number(formData.qtdParcela) || 1;
    const valorTotal = Number(formData.valorTotal) || 0;
    if (qtdParcela <= 0) return 0;
    return valorTotal / qtdParcela;
  };

  const toApiDate = (value) => {
    if (!value) return null;

    if (typeof value === "string") {
      if (value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        return value;
      }

      if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = value.split("-");
        return `${day}/${month}/${year}`;
      }
    }

    return formatarData(value);
  };

  const getSubGrupoSelecionado = (subGrupoId) => {
    if (!subGrupoId) return null;

    return (
      subGruposFiltrados.find(
        (subGrupo) => String(subGrupo.id) === String(subGrupoId)
      ) ||
      subGrupos.find(
        (subGrupo) => String(subGrupo.id) === String(subGrupoId)
      )
    );
  };

  const handleSubmit = async () => {
    if (!grupoSelecionadoId) {
      showError("Grupo e obrigatorio");
      return;
    }

    if (!formData.subgrupo.id) {
      showError("Subgrupo e obrigatorio");
      return;
    }

    if (!formData.descricao.trim()) {
      showError("Descrição é obrigatória");
      return;
    }

    // Trava síncrona: clique e Enter em sequência não podem enviar duas vezes,
    // porque o estado "loading" só muda no próximo render.
    if (envioEmAndamentoRef.current) {
      return;
    }
    envioEmAndamentoRef.current = true;

    setLoading(true);

    try {
      const subGrupoSelecionado = getSubGrupoSelecionado(formData.subgrupo.id);
      if (!subGrupoSelecionado?.id) {
        showError("Subgrupo invalido");
        return;
      }

      const dataToSend = {
        subGrupo: { id: String(subGrupoSelecionado.id) },
        descricao: formData.descricao,
        dataVencimento: toApiDate(formData.dataVencimento),
        valorTotal: formData.valorTotal || 0,
      };

      if (editingItem) {
        await despesaService.atualizarDespesas(editingItem.id, {
          ...dataToSend,
          observacao: formData.observacao,
        });
        showSuccess("Despesa atualizada com sucesso!");
      } else {
        // O cadastro novo não tem Observação e não a envia.
        await despesaService.cadastrarDespesas({
          ...dataToSend,
          qtdParcela: Number(formData.qtdParcela) || 0,
        });
        showSuccess("Despesa criada com sucesso!");
      }

      await loadDespesas();
      resetarFormulario();
    } catch (err) {
      // Em erro, o formulário mantém o que foi preenchido.
      showError(err.message || "Erro ao salvar despesa");
    } finally {
      envioEmAndamentoRef.current = false;
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    const subGrupoId =
      item.subgrupo?.id ||
      item.subgrupoId ||
      item.subGrupo?.id ||
      item.subGrupoId ||
      "";
    const grupoIdDireto =
      item.subgrupo?.grupo?.id ||
      item.subgrupo?.grupoId ||
      item.subGrupo?.grupo?.id ||
      item.subGrupo?.grupoId ||
      "";
    const grupoIdDoSubGrupo = subGrupos.find(
      (subGrupo) => String(subGrupo.id) === String(subGrupoId)
    )?.grupo?.id;
    const grupoId = grupoIdDireto || grupoIdDoSubGrupo || "";
    const qtdParcelaItem = getQtdParcelaDoItem(item);
    const valorTotal = Number(
      item.valorTotalDespesa || (Number(item.valorDespesa || 0) * qtdParcelaItem)
    );
    const valorTotalFormatted = formatarValorReal(
      (valorTotal * 100).toString()
    ).formatted;

    setEditingItem(item);
    setGrupoSelecionadoId(grupoId ? String(grupoId) : "");
    setFormData({
      subgrupo: { id: subGrupoId ? String(subGrupoId) : "" },
      descricao: item.descricao || "",
      dataVencimento: toInputDate(item.dataVencimento),
      qtdParcela: qtdParcelaItem,
      valorTotal,
      valorTotalFormatted,
      observacao: item.observacao || "",
    });

    if (formularioRef.current?.scrollIntoView) {
      formularioRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja excluir esta despesa?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);

    try {
      await despesaService.deletarDespesas(id);
      showSuccess("Despesa excluida com sucesso!");
      await loadDespesas();
    } catch (err) {
      showError(err.message || "Erro ao excluir despesa");
    } finally {
      setLoading(false);
    }
  };

  const handleAbrirModalDeletarMassa = (parcelaRef) => {
    if (!parcelaRef) {
      showError("Parcela referência não encontrada");
      return;
    }

    const despesasComMesmaParcelaRef = despesas.filter(
      (despesa) => despesa.parcelaRef === parcelaRef
    );

    if (despesasComMesmaParcelaRef.length === 0) {
      showError("Nenhuma despesa com essa referência encontrada");
      return;
    }

    setDespesasParaDeletarMassa(despesasComMesmaParcelaRef);
    setParcelaRefSelecionada(parcelaRef);
    setConfirmarDeletarMassaModalOpen(true);
    closeAcaoMenu();
  };

  const handleDeletarMassa = async () => {
    if (!parcelaRefSelecionada || despesasParaDeletarMassa.length === 0) {
      setConfirmarDeletarMassaModalOpen(false);
      return;
    }

    setLoading(true);

    try {
      await despesaService.deletarDespesasComParcelaRef(parcelaRefSelecionada);
      showSuccess(
        `${despesasParaDeletarMassa.length} despesa(s) deletada(s) com sucesso!`
      );
      setConfirmarDeletarMassaModalOpen(false);
      setDespesasParaDeletarMassa([]);
      setParcelaRefSelecionada(null);
      await loadDespesas();
    } catch (err) {
      showError(err.message || "Erro ao deletar despesas em massa");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelarDeletarMassa = () => {
    setConfirmarDeletarMassaModalOpen(false);
    setDespesasParaDeletarMassa([]);
    setParcelaRefSelecionada(null);
  };

  // Volta o formulário ao estado inicial: sai da edição e restaura os padrões
  // (parcelas = 1, valores zerados e vencimento na data local atual).
  const resetarFormulario = () => {
    setEditingItem(null);
    setGrupoSelecionadoId("");
    setSubGruposFiltrados([]);
    setFormData(criarFormularioInicial());
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const handleOpenPagamento = (item) => {
    const valorPago = Number(item.valorDespesa || 0);
    const valorPagoFormatted = formatarValorReal(
      (valorPago * 100).toString()
    ).formatted;
    setPagamentoItem(item);
    setPagamentoData({
      dataPagamento: new Date().toLocaleDateString("en-CA"),
      valorPago,
      valorPagoFormatted,
    });
    setShowPagamentoForm(true);
  };

  const handlePagamentoSubmit = async () => {
    if (!pagamentoItem) return;

    if (!pagamentoData.dataPagamento) {
      showError("Data de pagamento e obrigatoria");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        dataPagamento: toApiDate(pagamentoData.dataPagamento),
        valorPago: pagamentoData.valorPago || 0,
      };

      await despesaService.lancarPagamentoDespesas(pagamentoItem.id, payload);
      showSuccess("Pagamento lancado com sucesso!");
      await loadDespesas();
      setShowPagamentoForm(false);
      setPagamentoItem(null);
    } catch (err) {
      showError(err.message || "Erro ao lancar pagamento");
    } finally {
      setLoading(false);
    }
  };

  const handlePagamentoCancel = () => {
    setShowPagamentoForm(false);
    setPagamentoItem(null);
    setPagamentoData({
      dataPagamento: new Date().toLocaleDateString("en-CA"),
      valorPago: 0,
      valorPagoFormatted: "0,00",
    });
  };

  const handleOpenObservacao = (observacao) => {
    setObservacaoSelecionada(observacao || "");
    setObservacaoModalOpen(true);
  };

  const handleCloseObservacao = () => {
    setObservacaoSelecionada("");
    setObservacaoModalOpen(false);
  };

  const closeAcaoMenu = () => {
    setAcaoMenuAbertoId(null);
    setAcaoMenuPos(null);
    setAcaoMenuDirecao("down");
  };

  const toggleAcaoMenu = (id, event) => {
    if (!id || acaoMenuAbertoId === id) {
      closeAcaoMenu();
      return;
    }

    if (event?.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      const menuWidth = 176;
      const menuHeightEstimado = 220;
      const gap = 4;
      const espacoAbaixo = window.innerHeight - rect.bottom;
      const espacoAcima = rect.top;
      const left = Math.min(
        Math.max(8, rect.right - menuWidth),
        window.innerWidth - menuWidth - 8
      );
      const abrirParaCima =
        espacoAbaixo < menuHeightEstimado && espacoAcima > espacoAbaixo;
      const top = abrirParaCima ? rect.top - gap : rect.bottom + gap;
      setAcaoMenuPos({ top, left });
      setAcaoMenuDirecao(abrirParaCima ? "up" : "down");
    }

    setAcaoMenuAbertoId(id);
  };

  const getJuros = (item) => {
    const valorDespesa = Number(item.valorDespesa || 0);
    const valorPago = Number(item.valorPago || 0);
    return Math.abs(valorDespesa - valorPago);
  };

  const possuiDataPagamento = (item) => {
    const dataPagamento = item?.dataPagamento;

    if (!dataPagamento) {
      return false;
    }

    if (Array.isArray(dataPagamento)) {
      return dataPagamento.length >= 3;
    }

    if (typeof dataPagamento === "string") {
      const valor = dataPagamento.trim();
      return valor !== "" && valor !== "-";
    }

    const data = new Date(dataPagamento);
    return !Number.isNaN(data.getTime());
  };

  const handleApagarPagamento = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja reverter este pagamento?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);

    try {
      await despesaService.apagarPagamento(id);
      showSuccess("Pagamento revertido com sucesso!");
      await loadDespesas();
    } catch (err) {
      showError(err.message || "Erro ao apagar pagamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Ao entrar ou recarregar, o período padrão já é aplicado na consulta.
    loadDespesas(filtros);
    loadSubGrupos();
    loadGrupos();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!grupoSelecionadoId) {
      setSubGruposFiltrados([]);
      return;
    }

    loadSubGruposPorGrupo(grupoSelecionadoId);
  }, [grupoSelecionadoId]);

  const subGruposDoFiltro = useMemo(() => {
    if (!filtrosEmEdicao.grupoId) {
      return [];
    }

    return subGrupos.filter((subGrupo) => {
      const grupoIdDoSubGrupo = subGrupo?.grupo?.id ?? subGrupo?.grupoId ?? "";
      return String(grupoIdDoSubGrupo) === String(filtrosEmEdicao.grupoId);
    });
  }, [subGrupos, filtrosEmEdicao.grupoId]);

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Despesas</h1>
        <p className="text-gray-600">
          Gerencie as despesas cadastradas no sistema
        </p>
      </div>

      <div
        ref={formularioRef}
        className="mb-6 p-6 bg-gray-50 rounded-lg border"
      >
        <h3 className="text-lg font-semibold mb-4">
          {editingItem ? "Editar Despesa" : "Nova Despesa"}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Grupo" required htmlFor="grupo">
            <SelectInput
              id="grupo"
              name="grupoId"
              value={grupoSelecionadoId}
              onChange={handleGrupoChange}
            >
              <option value="">Selecione um grupo</option>
              {grupos.map((grupo) => (
                <option key={grupo.id} value={grupo.id}>
                  {grupo.nome}
                </option>
              ))}
            </SelectInput>
          </FormField>

          <FormField label="Subgrupo" required htmlFor="subgrupo">
            <SelectInput
              id="subgrupo"
              name="subgrupo"
              value={formData.subgrupo.id}
              onChange={handleFormChange}
              disabled={!grupoSelecionadoId}
            >
              <option value="">
                {grupoSelecionadoId
                  ? "Selecione um subgrupo"
                  : "Selecione um grupo primeiro"}
              </option>
              {subGruposFiltrados.map((subGrupo) => (
                <option key={subGrupo.id} value={subGrupo.id}>
                  {subGrupo.nome}
                </option>
              ))}
            </SelectInput>
          </FormField>

          <FormField className="md:col-span-2" label="Descrição" required htmlFor="descricao">
            <TextInput
              type="text"
              id="descricao"
              name="descricao"
              value={formData.descricao}
              onChange={handleFormChange}
              onKeyDown={handleKeyPress}
              placeholder="Digite a descricao"
              maxLength={200}
            />
          </FormField>

          <FormField label="Data de vencimento" htmlFor="dataVencimento">
            <TextInput
              type="date"
              id="dataVencimento"
              name="dataVencimento"
              value={formData.dataVencimento}
              onChange={handleFormChange}
            />
          </FormField>

          <FormField label="Valor total da despesa" htmlFor="valorTotal">
            <TextInput
              type="text"
              id="valorTotal"
              value={formData.valorTotalFormatted}
              onChange={handleValorTotalChange}
              onKeyDown={handleKeyPress}
              placeholder="0,00"
            />
          </FormField>

          {!editingItem && (
            <FormField label="Qtd. parcela" htmlFor="qtdParcela">
              <TextInput
                type="number"
                id="qtdParcela"
                name="qtdParcela"
                value={formData.qtdParcela}
                onChange={handleFormChange}
                min={1}
              />
            </FormField>
          )}

          <FormField label="Valor da despesa (calculado)" htmlFor="valorDespesaCalculado">
            <TextInput
              type="text"
              id="valorDespesaCalculado"
              value={formatCurrencyMoeda(getValorDespesaCalculadoFormulario())}
              readOnly
              className="border-gray-200 bg-gray-100 text-gray-700"
            />
          </FormField>

          {editingItem && (
            <FormField className="md:col-span-2" label="Observacao" htmlFor="observacao">
              <TextAreaInput
                id="observacao"
                name="observacao"
                value={formData.observacao}
                onChange={handleFormChange}
                rows={3}
                placeholder="Observacoes da despesa"
              />
            </FormField>
          )}
        </div>

        <div className="flex gap-2 mt-6">
          <Button
            onClick={handleSubmit}
            disabled={
              loading ||
              !grupoSelecionadoId ||
              !formData.subgrupo.id ||
              !formData.descricao.trim()
            }
            variant="success"
            icon={Save}
          >
            {loading ? "Salvando..." : "Salvar"}
          </Button>

          {editingItem && (
            <Button
              onClick={resetarFormulario}
              disabled={loading}
              variant="secondary"
              icon={X}
            >
              Cancelar edição
            </Button>
          )}
        </div>
      </div>

      <div className="mb-6 w-full p-4 bg-white border border-gray-200 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Filtros</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <FormField label="Data de vencimento - De" htmlFor="filtroDataDe" className="space-y-1">
            <TextInput
              type="date"
              id="filtroDataDe"
              name="dataDe"
              value={filtrosEmEdicao.dataDe}
              onChange={handleFiltroChange}
            />
          </FormField>

          <FormField label="Data de vencimento - Até" htmlFor="filtroDataAte" className="space-y-1">
            <TextInput
              type="date"
              id="filtroDataAte"
              name="dataAte"
              value={filtrosEmEdicao.dataAte}
              onChange={handleFiltroChange}
            />
          </FormField>

          <FormField label="Grupo" htmlFor="filtroGrupo" className="space-y-1">
            <SelectInput
              id="filtroGrupo"
              name="grupoId"
              value={filtrosEmEdicao.grupoId}
              onChange={handleFiltroChange}
            >
              <option value="">Todos</option>
              {grupos.map((grupo) => (
                <option key={grupo.id} value={grupo.id}>
                  {grupo.nome}
                </option>
              ))}
            </SelectInput>
          </FormField>

          <FormField label="Subgrupo" htmlFor="filtroSubGrupo" className="space-y-1">
            <SelectInput
              id="filtroSubGrupo"
              name="subGrupoId"
              value={filtrosEmEdicao.subGrupoId}
              onChange={handleFiltroChange}
              disabled={!filtrosEmEdicao.grupoId}
            >
              <option value="">
                {filtrosEmEdicao.grupoId ? "Todos" : "Selecione um grupo primeiro"}
              </option>
              {subGruposDoFiltro.map((subGrupo) => (
                <option key={subGrupo.id} value={subGrupo.id}>
                  {subGrupo.nome}
                </option>
              ))}
            </SelectInput>
          </FormField>

          <FormField label="Descrição" htmlFor="filtroDescricao" className="space-y-1">
            <TextInput
              type="text"
              id="filtroDescricao"
              name="descricao"
              value={filtrosEmEdicao.descricao}
              onChange={handleFiltroChange}
              placeholder="Descrição"
            />
          </FormField>

          <FormField label="Valor min" htmlFor="filtroValorMin" className="space-y-1">
            <TextInput
              type="text"
              id="filtroValorMin"
              name="valorMin"
              value={filtrosEmEdicao.valorMin}
              onChange={handleFiltroChange}
              placeholder="0,00"
            />
          </FormField>

          <FormField label="Valor max" htmlFor="filtroValorMax" className="space-y-1">
            <TextInput
              type="text"
              id="filtroValorMax"
              name="valorMax"
              value={filtrosEmEdicao.valorMax}
              onChange={handleFiltroChange}
              placeholder="0,00"
            />
          </FormField>

          <FormField label="Situacao" htmlFor="filtroSituacao" className="space-y-1">
            <SelectInput
              id="filtroSituacao"
              name="situacao"
              value={filtrosEmEdicao.situacao}
              onChange={handleFiltroChange}
            >
              <option value="">Todas</option>
              <option value="PAGO">Pago</option>
              <option value="ATRASADO">Atrasado</option>
              <option value="PENDENTE">Pendente</option>
            </SelectInput>
          </FormField>
        </div>

        <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <Button
            onClick={handleLimparFiltros}
            disabled={loading}
            variant="secondary"
          >
            Limpar filtros
          </Button>

          <Button onClick={handleBuscarFiltros} disabled={loading}>
            Buscar
          </Button>
        </div>
      </div>

      {showPagamentoForm && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">Pagar Parcela</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Data de pagamento" required htmlFor="pagamentoData">
              <TextInput
                type="date"
                id="pagamentoData"
                value={pagamentoData.dataPagamento}
                onChange={(e) =>
                  setPagamentoData((prev) => ({
                    ...prev,
                    dataPagamento: e.target.value,
                  }))
                }
              />
            </FormField>

            <FormField label="Valor pago" htmlFor="pagamentoValor">
              <TextInput
                type="text"
                id="pagamentoValor"
                value={pagamentoData.valorPagoFormatted}
                onChange={handlePagamentoValorChange}
                placeholder="0,00"
              />
            </FormField>
          </div>

          <div className="flex gap-2 mt-6">
            <Button
              onClick={handlePagamentoSubmit}
              disabled={loading || !pagamentoData.dataPagamento}
              variant="success"
              icon={Save}
            >
              {loading ? "Salvando..." : "Confirmar"}
            </Button>

            <Button
              onClick={handlePagamentoCancel}
              variant="secondary"
              icon={X}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {observacaoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white w-full max-w-lg rounded-lg shadow-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-lg font-semibold">Observacao</h3>
              <button
                onClick={handleCloseObservacao}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Fechar"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-4 py-4">
              <p className="text-gray-700 whitespace-pre-line">
                {observacaoSelecionada || "-"}
              </p>
            </div>
            <div className="px-4 py-3 border-t flex justify-end">
              <button
                onClick={handleCloseObservacao}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmarDeletarMassaModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white w-full max-w-lg rounded-lg shadow-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-lg font-semibold text-red-600">Deletar Despesas em Massa</h3>
              <button
                onClick={handleCancelarDeletarMassa}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Fechar"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-4 py-4">
              <p className="text-gray-700 mb-4">
                Tem certeza que deseja deletar <strong>{despesasParaDeletarMassa.length}</strong> despesa(s) com a parcela referência <strong>{parcelaRefSelecionada}</strong>?
              </p>
              {despesasParaDeletarMassa.length > 0 && (
                <div className="bg-gray-50 rounded p-3 mb-4 max-h-40 overflow-y-auto">
                  <p className="text-xs font-semibold text-gray-600 mb-2">Despesas que serão deletadas:</p>
                  <ul className="space-y-1">
                    {despesasParaDeletarMassa.map((despesa) => (
                      <li key={despesa.id} className="text-xs text-gray-700">
                        • {despesa.descricao} - {formatCurrencyMoeda(despesa.valorDespesa)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-sm text-red-600 font-semibold">
                Esta ação não pode ser desfeita!
              </p>
            </div>
            <div className="px-4 py-3 border-t flex justify-end gap-2">
              <button
                onClick={handleCancelarDeletarMassa}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeletarMassa}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Trash2 size={16} />
                {loading ? "Deletando..." : "Deletar"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white">
        <div className="p-4 flex justify-between items-center">
          <h3 className="text-lg font-semibold">Lista de Despesas</h3>
          <span className="text-sm text-gray-500">
            {totalRegistros} registro(s) encontrado(s)
          </span>
        </div>

        <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500">Total despesas</p>
            <p className="text-sm font-semibold text-gray-900">
              {formatCurrencyMoeda(resumoDespesas.sumValorDespesa)}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500">Total pago</p>
            <p className="text-sm font-semibold text-gray-900">
              {formatCurrencyMoeda(resumoDespesas.sumValorPago)}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500">Total pendente</p>
            <p className="text-sm font-semibold text-gray-900">
              {formatCurrencyMoeda(resumoDespesas.sumPendente)}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500">Total juros</p>
            <p className="text-sm font-semibold text-gray-900">
              {formatCurrencyMoeda(Math.abs(Number(resumoDespesas.sumJuros || 0)))}
            </p>
          </div>
        </div>

        {loading && despesas.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Carregando despesas...
          </div>
        ) : despesas.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {temFiltrosAtivos
              ? "Nenhuma despesa encontrada"
              : "Nenhuma despesa cadastrada"}
          </div>
        ) : (
          <>
            <div
              className={`overflow-x-auto ${
                filtrosAplicadosNaLista ? "max-h-[70vh] overflow-y-auto" : ""
              }`}
            >
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                      Descrição
                    </th>
                    <th className="px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                      Data lancamento
                    </th>
                    <th className="px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                      Data vencimento
                    </th>
                    <th className="px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                      Data pagamento
                    </th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                      Valor pago
                    </th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                      Juros
                    </th>
                    <th className="px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                      Parcela
                    </th>
                    <th className="px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                      Situacao
                    </th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                      Valor despesa
                    </th>
                    <th className="px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                      Observacao
                    </th>
                    <th className="px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider whitespace-normal">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {despesasExibidas.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 align-top text-center">
                        <div className="text-xs font-medium text-gray-900">
                          {item.descricao}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-center">
                        <div className="text-xs text-gray-700">
                          {formatarData(item.dataLancamento)}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-center">
                        <div className="text-xs text-gray-700">
                          {formatarData(item.dataVencimento)}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-center">
                        <div className="text-xs text-gray-700">
                          {formatarData(item.dataPagamento)}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        <div className="text-xs text-gray-700">
                          {formatCurrencyMoeda(item.valorPago)}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        <div className="text-xs text-gray-700">
                          {formatCurrencyMoeda(
                            item.juros !== undefined && item.juros !== null
                              ? Math.abs(Number(item.juros || 0))
                              : getJuros(item)
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-center">
                        <div className="text-xs text-gray-700">
                          {item.parcela || "-"}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-center">
                        <div className={`text-xs font-medium ${getSituacaoClasseCor(item.situacao)}`}>
                          {item.situacao || "-"}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        <div className="text-xs text-gray-700">
                          {formatCurrencyMoeda(item.valorDespesa)}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-center">
                        {item.observacao ? (
                          <button
                            onClick={() => handleOpenObservacao(item.observacao)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors"
                            title="Ver observacao"
                          >
                            <Eye size={16} />
                          </button>
                        ) : (
                          <div className="text-xs text-gray-700">-</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center text-xs font-medium align-top">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => {
                              if (possuiDataPagamento(item)) {
                                handleApagarPagamento(item.id);
                                return;
                              }

                              handleOpenPagamento(item);
                            }}
                            disabled={loading}
                            className={`px-2.5 py-1 rounded-md transition-colors text-white ${
                              possuiDataPagamento(item)
                                ? "bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400"
                                : "bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400"
                            }`}
                            title={possuiDataPagamento(item) ? "Reverter pagamento" : "Pagar parcela"}
                          >
                            {possuiDataPagamento(item) ? "Reverter" : "Pagar"}
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            disabled={loading}
                            className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-2.5 py-1 rounded-md transition-colors"
                            title="Excluir"
                          >
                            Excluir
                          </button>
                          <button
                            onClick={(e) => toggleAcaoMenu(item.id, e)}
                            disabled={loading}
                            className="bg-gray-100 hover:bg-gray-200 disabled:bg-gray-100 text-gray-700 p-1.5 rounded-md transition-colors"
                            title="Mais acoes"
                          >
                            <MoreHorizontal size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!filtrosAplicadosNaLista && totalPaginas > 1 && (
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Mostrando {indiceInicial + 1} ate {Math.min(indiceFinal, totalRegistros)} de {totalRegistros} registros
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={irParaPrimeiraPagina}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Primeira página"
                    >
                      <ChevronsLeft size={16} />
                    </button>

                    <button
                      onClick={irParaPaginaAnterior}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Página anterior"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    <div className="flex space-x-1">
                      {gerarNumerosPaginas().map((numeroPagina) => (
                        <button
                          key={numeroPagina}
                          onClick={() => irParaPagina(numeroPagina)}
                          className={`px-3 py-2 rounded-md text-sm font-medium ${
                            paginacao.paginaAtual === numeroPagina
                              ? "bg-blue-600 text-white"
                              : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {numeroPagina}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={irParaProximaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Próxima página"
                    >
                      <ChevronRight size={16} />
                    </button>

                    <button
                      onClick={irParaUltimaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Última página"
                    >
                      <ChevronsRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {acaoMenuAbertoId && acaoMenuPos && (
        <div className="fixed inset-0 z-50" onClick={closeAcaoMenu}>
          <div
            className="fixed w-44 max-h-[70vh] bg-white border border-gray-200 rounded-md shadow-lg flex flex-col overflow-y-auto"
            style={{
              top: acaoMenuPos.top,
              left: acaoMenuPos.left,
              transform:
                acaoMenuDirecao === "up" ? "translateY(-100%)" : "translateY(0)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                closeAcaoMenu();
                const item = despesas.find(
                  (despesa) => despesa.id === acaoMenuAbertoId
                );
                if (item) handleEdit(item);
              }}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Edit2 size={14} />
              Editar
            </button>
            <button
              onClick={() => {
                const item = despesas.find(
                  (despesa) => despesa.id === acaoMenuAbertoId
                );
                if (item) handleAbrirModalDeletarMassa(item.parcelaRef);
              }}
              className="w-full text-left px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2"
            >
              <Trash2 size={14} />
              Deletar em massa
            </button>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default Despesa;
